/**
 * DateInput — React (web) implementation.
 *
 * A fieldset with a visible legend and three numeric boxes, Month, Day and
 * Year. Typing the last digit a box takes moves to the next box, Backspace in
 * an empty box goes back, and a whole date pasted into any box fills all
 * three. Every edit reports one value: the parts plus an ISO date when they
 * make a real calendar date (worked out with Luxon in DateInput.ts).
 * Disabled stays focusable (read-only plus aria-disabled).
 * All styling references the shared constants in DateInput.ts.
 */

import React, { useId, useRef, useState } from 'react';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import { FieldError } from '../FieldError/FieldError.web';
import { hasFieldErrorMessage } from '../FieldError/FieldError';
import {
  dateInputBoxColorsFor,
  dateInputPartDigits,
  dateInputPartsFromPaste,
  dateInputTextColorsFor,
  dateInputValueOf,
  DATE_INPUT_BORDER_WIDTH,
  DATE_INPUT_BOX_GAP,
  DATE_INPUT_BOX_HEIGHT,
  DATE_INPUT_BOX_HORIZONTAL_PADDING,
  DATE_INPUT_BOX_RADIUS,
  DATE_INPUT_DISABLED_PLACEHOLDER_COLOR,
  DATE_INPUT_EMPTY_PARTS,
  DATE_INPUT_HELPER_TYPOGRAPHY,
  DATE_INPUT_LABEL_VALUE_GAP,
  DATE_INPUT_LEGEND_TYPOGRAPHY,
  DATE_INPUT_PART_LABEL_TYPOGRAPHY,
  DATE_INPUT_PART_MAX_DIGITS,
  DATE_INPUT_PART_ORDER,
  DATE_INPUT_PLACEHOLDER_COLOR,
  DATE_INPUT_SECTION_GAP,
  DATE_INPUT_VALUE_TYPOGRAPHY,
  DATE_INPUT_YEAR_WIDTH,
} from './DateInput';
import type { DateInputPart, DateInputParts, DateInputValue } from './DateInput';
import type { TypeStyle } from '../../elements/typography';

const DATE_INPUT_STYLE_ID = 'galapagos-date-input-styles';
const DATE_INPUT_BOX_SELECTOR = '[data-galapagos-date-input-box="true"]';

// The ring is drawn on the box, not the bare <input>, so it wraps the part
// label too (as v7 draws it). `:has(:focus-visible)` keeps it keyboard-driven
// (ADR-025 Rule 2); the input's own outline is only removed where `:has()` is
// supported, so an older browser still shows its native focus outline.
// Placeholders take the v7 colour; a disabled box mutes them.
function ensureDateInputStyle(): void {
  ensureStyle(
    DATE_INPUT_STYLE_ID,
    `${DATE_INPUT_BOX_SELECTOR} input::placeholder { color: ${DATE_INPUT_PLACEHOLDER_COLOR}; opacity: 1; }` +
      ` ${DATE_INPUT_BOX_SELECTOR}[data-disabled="true"] input::placeholder { color: ${DATE_INPUT_DISABLED_PLACEHOLDER_COLOR}; }` +
      ` @supports selector(:has(*)) {` +
      ` ${DATE_INPUT_BOX_SELECTOR} input:focus { outline: none; }` +
      ` ${DATE_INPUT_BOX_SELECTOR}:has(:focus-visible) { ${FOCUS_RING_OUTLINE} }` +
      ` }`,
  );
}

function textStyle(type: TypeStyle, color: string): React.CSSProperties {
  return {
    fontFamily: type.fontFamily,
    fontWeight: type.fontWeight,
    fontSize: type.fontSize,
    lineHeight: `${type.lineHeight}px`,
    letterSpacing: type.letterSpacing,
    color,
  };
}

const AUTOCOMPLETE_BY_PART: Readonly<Record<DateInputPart, string>> = {
  month: 'bday-month',
  day: 'bday-day',
  year: 'bday-year',
};

export interface DateInputCopy {
  month?: string;
  day?: string;
  year?: string;
}

export interface WebDateInputProps {
  /** The fieldset's visible legend, e.g. "Date of birth". */
  legend?: string;
  /** Small label above each box, e.g. Month / Day / Year. */
  partLabels?: DateInputCopy;
  /** Placeholder in each empty box, e.g. MM / DD / YYYY. */
  partPlaceholders?: DateInputCopy;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  /** `bday` marks the boxes as a birthday for browser autofill. */
  autoComplete?: 'bday' | 'off';
  /** Controlled parts. */
  value?: DateInputParts;
  /** Initial parts when uncontrolled. */
  defaultValue?: DateInputParts;
  autoFocus?: boolean;
  /** Form-field name prefix; the boxes post as `{name}-month`, `-day`, `-year`. */
  name?: string;
  /** Domain action id for the change interaction (ADR-081 Rule 4). */
  actionId?: string;
  /** Receives the parts and their ISO date after every edit. */
  onChangeValue?: (value: DateInputValue) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function DateInput({
  legend,
  partLabels,
  partPlaceholders,
  helperText,
  errorText,
  disabled = false,
  autoComplete = 'off',
  value,
  defaultValue,
  autoFocus = false,
  name,
  actionId = 'ui.date-input.change',
  onChangeValue,
  onBlur,
  onFocus,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebDateInputProps) {
  ensureDateInputStyle();

  const generatedId = useId();
  const baseId = testId ? `${testId}-date` : `date-input-${generatedId}`;
  const helperId = `${baseId}-helper`;
  const errorId = `${baseId}-error`;
  const inputId = (part: DateInputPart) => `${baseId}-${part}`;

  const [ownParts, setOwnParts] = useState<DateInputParts>(defaultValue ?? DATE_INPUT_EMPTY_PARTS);
  const parts = value ?? ownParts;
  // The latest parts, including an edit not yet rendered: autofill writes the
  // three boxes back to back, and each write must build on the one before.
  // Re-synced only when the parts themselves change, so an unrelated re-render
  // (focus moving to the next box) cannot drop a pending edit.
  const latestParts = useRef(parts);
  const syncedParts = useRef(parts);
  if (syncedParts.current !== parts) {
    syncedParts.current = parts;
    latestParts.current = parts;
  }

  const [focusedPart, setFocusedPart] = useState<DateInputPart | null>(null);
  const [hoveredPart, setHoveredPart] = useState<DateInputPart | null>(null);
  const inputRefs = useRef<Partial<Record<DateInputPart, HTMLInputElement | null>>>({});

  const hasError = hasFieldErrorMessage(errorText);
  const showHelper = !hasError && Boolean(helperText?.trim());
  const textColors = dateInputTextColorsFor(disabled);
  const describedBy = hasError ? errorId : showHelper ? helperId : undefined;

  const report = (next: DateInputParts) => {
    latestParts.current = next;
    if (value === undefined) setOwnParts(next);
    onChangeValue?.(dateInputValueOf(next));
  };

  const focusPart = (part: DateInputPart | undefined, caretAtEnd = false) => {
    if (!part) return;
    const input = inputRefs.current[part];
    if (!input) return;
    input.focus();
    if (caretAtEnd) input.setSelectionRange(input.value.length, input.value.length);
  };

  const nextPart = (part: DateInputPart) =>
    DATE_INPUT_PART_ORDER[DATE_INPUT_PART_ORDER.indexOf(part) + 1];
  const previousPart = (part: DateInputPart) =>
    DATE_INPUT_PART_ORDER[DATE_INPUT_PART_ORDER.indexOf(part) - 1];

  const handleChange = (part: DateInputPart, text: string) => {
    if (disabled) return;
    const current = latestParts.current;
    const digits = dateInputPartDigits(part, text);
    if (digits === current[part]) return;
    report({ ...current, [part]: digits });
    if (
      digits.length === DATE_INPUT_PART_MAX_DIGITS[part] &&
      digits.length > current[part].length
    ) {
      focusPart(nextPart(part));
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    const pasted = dateInputPartsFromPaste(event.clipboardData.getData('text/plain'));
    if (!pasted) return;
    event.preventDefault();
    report(pasted);
    focusPart('year', true);
  };

  const handleKeyDown = (part: DateInputPart, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || event.key !== 'Backspace' || latestParts.current[part] !== '') return;
    const previous = previousPart(part);
    if (!previous) return;
    event.preventDefault();
    focusPart(previous, true);
  };

  const fieldsetStyle: React.CSSProperties = {
    margin: 0,
    padding: 0,
    border: 'none',
    minWidth: 0,
  };

  const legendStyle: React.CSSProperties = {
    ...textStyle(DATE_INPUT_LEGEND_TYPOGRAPHY, textColors.legend),
    padding: 0,
    marginBottom: DATE_INPUT_SECTION_GAP,
  };

  const renderBox = (part: DateInputPart) => {
    const colors = dateInputBoxColorsFor({
      disabled,
      hasError,
      focused: focusedPart === part && !disabled,
      hovered: hoveredPart === part && !disabled,
    });
    const id = inputId(part);
    const boxStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: DATE_INPUT_LABEL_VALUE_GAP,
      boxSizing: 'border-box',
      height: DATE_INPUT_BOX_HEIGHT,
      minWidth: 0,
      paddingLeft: DATE_INPUT_BOX_HORIZONTAL_PADDING,
      paddingRight: DATE_INPUT_BOX_HORIZONTAL_PADDING,
      backgroundColor: colors.background,
      border: `${DATE_INPUT_BORDER_WIDTH}px solid ${colors.border}`,
      borderRadius: DATE_INPUT_BOX_RADIUS,
      cursor: disabled ? 'not-allowed' : 'text',
      ...(part === 'year'
        ? { width: DATE_INPUT_YEAR_WIDTH, flexShrink: 0 }
        : { flexGrow: 1, flexShrink: 1, flexBasis: 0 }),
    };
    const inputStyle: React.CSSProperties = {
      ...textStyle(DATE_INPUT_VALUE_TYPOGRAPHY, colors.value),
      margin: 0,
      padding: 0,
      border: 'none',
      background: 'transparent',
      width: '100%',
      minWidth: 0,
      cursor: 'inherit',
    };
    return (
      // analytics-exempt: forwarded-wrapper
      <div
        key={part}
        style={boxStyle}
        data-galapagos-date-input-box="true"
        data-disabled={disabled || undefined}
        data-testid={testId ? `${testId}-${part}-box` : undefined}
        // A press anywhere on the box (its label included) lands on the input.
        onClick={() => inputRefs.current[part]?.focus()}
        {...mouseHoverHandlers((over) =>
          setHoveredPart((current) => (over ? part : current === part ? null : current)),
        )}
      >
        <label
          htmlFor={id}
          style={textStyle(DATE_INPUT_PART_LABEL_TYPOGRAPHY, colors.label)}
          data-testid={testId ? `${testId}-${part}-label` : undefined}
        >
          {partLabels?.[part]}
        </label>
        <input
          ref={(element) => {
            inputRefs.current[part] = element;
          }}
          data-analytics-action={actionId}
          id={id}
          name={name ? `${name}-${part}` : undefined}
          type="text"
          inputMode="numeric"
          autoComplete={autoComplete === 'bday' ? AUTOCOMPLETE_BY_PART[part] : 'off'}
          value={parts[part]}
          placeholder={partPlaceholders?.[part] || undefined}
          // Disabled stays focusable (ADR-025 Rule 3): read-only plus
          // aria-disabled instead of the native `disabled` attribute.
          readOnly={disabled}
          autoFocus={autoFocus && part === 'month'}
          onChange={(event) => handleChange(part, event.target.value)}
          onPaste={handlePaste}
          onKeyDown={(event) => handleKeyDown(part, event)}
          onFocus={() => {
            setFocusedPart(part);
            onFocus?.();
          }}
          onBlur={() => {
            setFocusedPart((current) => (current === part ? null : current));
            onBlur?.();
          }}
          style={inputStyle}
          aria-invalid={hasError}
          aria-disabled={disabled || undefined}
          aria-describedby={describedBy}
          data-testid={testId ? `${testId}-${part}` : undefined}
        />
      </div>
    );
  };

  return (
    <fieldset
      style={fieldsetStyle}
      aria-label={ariaLabel || undefined}
      data-testid={testId ? `${testId}-container` : undefined}
    >
      <legend style={legendStyle} data-testid={testId ? `${testId}-legend` : undefined}>
        {legend}
      </legend>
      <div style={{ display: 'flex', gap: DATE_INPUT_BOX_GAP }}>
        {DATE_INPUT_PART_ORDER.map(renderBox)}
      </div>
      {showHelper ? (
        <div
          id={helperId}
          style={{
            ...textStyle(DATE_INPUT_HELPER_TYPOGRAPHY, textColors.helper),
            paddingTop: DATE_INPUT_SECTION_GAP,
          }}
          data-testid={testId ? `${testId}-helper` : undefined}
        >
          {helperText}
        </div>
      ) : null}
      {/* Always present, so the error is announced when it appears inside it. */}
      <div aria-live="polite" data-testid={testId ? `${testId}-live` : undefined}>
        {hasError ? (
          <div style={{ paddingTop: DATE_INPUT_SECTION_GAP }}>
            <FieldError
              live={false}
              message={errorText}
              id={errorId}
              data-testid={testId ? `${testId}-error` : undefined}
            />
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
